import React from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { FileText, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

export const ImplementationModal = ({
  open,
  onOpenChange,
  implementationData,
  loading = false,
  selectedItem,
  onSelectItem,
  onUpdateItem,
  canEdit = false,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      default: return 'bg-slate-400';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Implementation Plan
          </DialogTitle>
          <DialogDescription>
            Feature implementation details and progress tracking {!canEdit && '(Read Only)'}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Metrics Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-600">{implementationData.metrics?.progress_percent || 0}%</p>
                <p className="text-xs text-purple-700 dark:text-purple-300">Overall Progress</p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{implementationData.metrics?.completed || 0}</p>
                <p className="text-xs text-green-700 dark:text-green-300">Completed</p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{implementationData.metrics?.in_progress || 0}</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">In Progress</p>
              </div>
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-600">{implementationData.metrics?.pending || 0}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
            
            {/* Implementation Items List */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Implementation Items ({implementationData.items?.length || 0})
              </h3>
              <div className="flex gap-4">
                {/* Items List */}
                <ScrollArea className={`${selectedItem ? 'w-1/2' : 'w-full'} h-[400px]`}>
                  <div className="space-y-2 pr-2">
                    {implementationData.items?.map((item, idx) => (
                      <div 
                        key={item.id || idx}
                        onClick={() => onSelectItem(item)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedItem?.id === item.id 
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                            : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm truncate">{item.name}</h4>
                          <Badge className={`text-[10px] ${getStatusColor(item.status)}`}>
                            {item.status?.replace('_', ' ') || 'pending'}
                          </Badge>
                        </div>
                        {item.deployment_date && (
                          <p className="text-xs text-purple-600 dark:text-purple-400">Deploy: {item.deployment_date}</p>
                        )}
                      </div>
                    ))}
                    {(!implementationData.items || implementationData.items.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        No implementation items yet. Add features via Scope to populate.
                      </div>
                    )}
                  </div>
                </ScrollArea>
                
                {/* Detail Panel */}
                {selectedItem && (
                  <div className="w-1/2 border-l border-slate-200 dark:border-slate-700 pl-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200">{selectedItem.name}</h4>
                      <Button variant="ghost" size="sm" onClick={() => onSelectItem(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Status */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Status</label>
                        {canEdit ? (
                          <select
                            value={selectedItem.status || 'pending'}
                            onChange={(e) => onUpdateItem(selectedItem.id, { status: e.target.value })}
                            className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        ) : (
                          <p className="mt-1 p-2 text-sm capitalize bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.status?.replace('_', ' ') || 'Pending'}
                          </p>
                        )}
                      </div>
                      
                      {/* Deployment Target */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Deployment Target</label>
                        {canEdit ? (
                          <select
                            value={selectedItem.deployment_target || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { deployment_target: e.target.value })}
                            className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                          >
                            <option value="">Select target...</option>
                            <option value="development">Development</option>
                            <option value="staging">Staging</option>
                            <option value="production">Production</option>
                          </select>
                        ) : (
                          <p className="mt-1 p-2 text-sm capitalize bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.deployment_target || 'Not set'}
                          </p>
                        )}
                      </div>
                      
                      {/* Deployment Date */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Deployment Date</label>
                        {canEdit ? (
                          <Input
                            type="date"
                            value={selectedItem.deployment_date || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { deployment_date: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.deployment_date || 'Not set'}
                          </p>
                        )}
                      </div>
                      
                      {/* Requirements */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">What&apos;s needed to make it work</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="List requirements (one per line)"
                            value={(selectedItem.requirements || []).join('\n')}
                            onChange={(e) => onUpdateItem(selectedItem.id, { 
                              requirements: e.target.value.split('\n').filter(r => r.trim())
                            })}
                            className="mt-1 min-h-[80px]"
                          />
                        ) : (
                          <div className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[80px]">
                            {selectedItem.requirements?.length > 0 ? (
                              <ul className="list-disc list-inside">
                                {selectedItem.requirements.map((req, i) => (
                                  <li key={i}>{req}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted-foreground">No requirements listed</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Deployment Notes */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Deployment Notes</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Notes about deployment..."
                            value={selectedItem.deployment_notes || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { deployment_notes: e.target.value })}
                            className="mt-1 min-h-[60px]"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px]">
                            {selectedItem.deployment_notes || 'No notes'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImplementationModal;
